import React, { useState, useCallback } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Search, Briefcase } from 'lucide-react';
import { UnidadNegocio } from '../../types';
import ApiService from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';

interface UnidadesNegocioConfigProps {
  onBack: () => void;
}

interface UnidadNegocioFormData {
  nombre: string;
  descripcion: string;
}

const UnidadesNegocioConfig: React.FC<UnidadesNegocioConfigProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUnidadNegocio, setEditingUnidadNegocio] = useState<UnidadNegocio | null>(null);
  const [formData, setFormData] = useState<UnidadNegocioFormData>({ nombre: '', descripcion: '' });

  // Memoizar función de API para evitar recreaciones
  const fetchUnidadesNegocio = useCallback(() => ApiService.getUnidadesNegocio(), []);

  // Hooks para datos y operaciones
  const { data: unidadesNegocio, loading, error, refetch } = useApi(fetchUnidadesNegocio);
  const { execute: createUnidadNegocio, loading: creating } = useAsyncOperation();
  const { execute: updateUnidadNegocio, loading: updating } = useAsyncOperation();
  const { execute: deleteUnidadNegocio } = useAsyncOperation();

  // Filtrar unidades de negocio por búsqueda
  const filteredUnidadesNegocio = unidadesNegocio?.filter(unidad =>
    unidad.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (unidad.descripcion && unidad.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Manejar apertura de modal para crear/editar
  const handleOpenModal = (unidadNegocio?: UnidadNegocio) => {
    if (unidadNegocio) {
      setEditingUnidadNegocio(unidadNegocio);
      setFormData({ nombre: unidadNegocio.nombre, descripcion: unidadNegocio.descripcion || '' });
    } else {
      setEditingUnidadNegocio(null);
      setFormData({ nombre: '', descripcion: '' });
    }
    setIsModalOpen(true);
  };

  // Manejar cierre de modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUnidadNegocio(null);
    setFormData({ nombre: '', descripcion: '' });
  };

  // Manejar envío de formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const operation = editingUnidadNegocio 
      ? () => ApiService.updateUnidadNegocio(editingUnidadNegocio.id, formData)
      : () => ApiService.createUnidadNegocio(formData);
    
    const execute = editingUnidadNegocio ? updateUnidadNegocio : createUnidadNegocio;
    
    await execute(operation);
    handleCloseModal();
    refetch();
  };

  // Manejar eliminación
  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta unidad de negocio?')) {
      await deleteUnidadNegocio(() => ApiService.deleteUnidadNegocio(id));
      refetch();
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">Gestión de Unidades de Negocio</h2>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover flex items-center gap-2"
          disabled={creating}
        >
          <Plus size={16} />
          Nueva Unidad de Negocio
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={16} />
        <input
          type="text"
          placeholder="Buscar unidades de negocio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 input-field w-full"
        />
      </div>

      {/* Lista de Unidades de Negocio */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUnidadesNegocio.map((unidadNegocio) => (
          <div
            key={unidadNegocio.id}
            className="card p-6 transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                  style={{ backgroundColor: unidadNegocio.color || '#3B82F6' }}
                >
                  <Briefcase size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">{unidadNegocio.nombre}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{unidadNegocio.areasCount} áreas</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleOpenModal(unidadNegocio)}
                  className="p-1 text-neutral-400 dark:text-neutral-500 hover:text-primary"
                  disabled={updating}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(unidadNegocio.id)}
                  className="p-1 text-neutral-400 dark:text-neutral-500 hover:text-error"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {unidadNegocio.descripcion && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">{unidadNegocio.descripcion}</p>
            )}
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingUnidadNegocio ? 'Editar Unidad de Negocio' : 'Nueva Unidad de Negocio'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="w-full px-3 py-2 input-field"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="btn-primary disabled:opacity-50"
                >
                  {creating || updating ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnidadesNegocioConfig;